<script setup lang="ts">
import { computed } from 'vue';
import { t } from '@nova/core';

import type { CustomFieldProps } from '../types';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';
import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import { useCustomFieldValidation } from '@satellite/composables/useCustomFieldValidation';

/**
 * Boolean field type - toggle switch
 */
const {
  hideDetails = 'auto',
  displayLabel = true,
  required = undefined,
  ...props
} = defineProps<CustomFieldProps>();

const emit = defineEmits<{
  'update:model-value': [value: boolean];
  /** @deprecated Use update:model-value instead */
  input: [value: boolean];
}>();

const { normalizedProps } = useCustomFieldProps(() => ({
  ...props,
  hideDetails,
  displayLabel,
  required
}));
const { rules } = useCustomFieldValidation(
  computed(() => ({ ...props.field, required: normalizedProps.required }))
);

const label = computed(() =>
  props.modelValue ? t('global.basicTerms.yes') : t('global.basicTerms.no')
);

function handleInput(value: boolean) {
  emit('update:model-value', value);
  emit('input', value);
}
</script>

<template>
  <custom-field-input-base :field="field" v-bind="normalizedProps">
    <v-switch
      :data-testid="testId"
      inset
      color="primary"
      density="comfortable"
      :hide-details="normalizedProps.hideDetails"
      :label="label"
      :model-value="modelValue"
      :readonly="normalizedProps.readOnly"
      :rules="rules"
      @update:model-value="handleInput" />
  </custom-field-input-base>
</template>
